import React, { useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from './ui/command';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

// Searchable client combobox (~240 clients). Hides inactive clients except the
// one already selected (edit mode). Search: name, GSTIN, contact, phone, email.
// modal: keeps the search input focusable + the list scrollable inside a Dialog.
export default function ClientPicker({ clients = [], value, onChange, placeholder = 'Pick a client' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);

  const options = useMemo(
    () => clients
      .filter((c) => c.status !== 'inactive' || c.id === value)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [clients, value],
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((c) =>
      [c.name, c.gstin, c.contact_person, c.phone, (c.phone || '').replace(/\D/g, ''), c.email]
        .join(' ')
        .toLowerCase()
        .includes(q));
  }, [options, query]);

  const selected = clients.find((c) => c.id === value);

  const toggle = (o) => {
    setOpen(o);
    if (!o) setQuery('');
  };

  const pick = (id) => {
    onChange(id);
    toggle(false);
  };

  return (
    <Popover open={open} onOpenChange={toggle} modal>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between font-normal h-10', !selected && 'text-gray-400')}
        >
          <span className="truncate">{selected ? selected.name : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 z-[200] bg-white shadow-xl border w-[--radix-popover-trigger-width] min-w-[260px]"
        align="start"
        sideOffset={6}
        // Chrome fires the Enter keypress on whatever has focus right after cmdk
        // selects on keydown; refocusing the trigger immediately re-opened the list.
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => triggerRef.current?.focus(), 150);
        }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search name, GSTIN, contact, phone…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>No client found.</CommandEmpty>
            {shown.map((c) => (
              <CommandItem key={c.id} value={c.id} onSelect={() => pick(c.id)}>
                <Check className={cn('h-4 w-4', c.id === value ? 'opacity-100' : 'opacity-0')} />
                <div className="min-w-0">
                  <p className="truncate">{c.name}</p>
                  {(c.gstin || c.bill_to_city || c.contact_person) && (
                    <p className="truncate text-xs text-gray-500">{c.gstin || c.bill_to_city || c.contact_person}</p>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
